/**
 * Inventory (07) — server response shapes (mirror of the `inventory` module, Magasinier surface).
 *
 * The server returns the standard { success, data } envelope; the api/useApiQuery layer unwraps `data`,
 * so these types describe the UNWRAPPED payload. BigInt ids serialize to JSON numbers/strings, so every
 * id is typed `string | number` and normalized to a string with `idStr()` before use as a key/param.
 *
 * Only the fields the screens read are typed; the server may carry more keys (forward-compatible). Where
 * a contract is uncertain we keep a field optional and degrade gracefully rather than invent it.
 */

/** A normalized id — the wire carries either, callers compare/scope on the string form. */
export type Id = string | number;

/** Coerce a wire id to a stable string (BigInt-safe; never produces "[object Object]"). */
export function idStr(id: Id | null | undefined): string {
  return id == null ? "" : String(id);
}

// ── Reactif catalog ──────────────────────────────────────────────────────────────

export type UniteMesure = "test" | "ml" | "kit";
export type ReactifStatut = "actif" | "inactif" | string;

/** A reagent in the catalog (GET /inventory/reactifs row). */
export interface Reactif {
  id: Id;
  code: string;
  libelle: string;
  discipline_code: string;
  automate_id?: Id | null;
  unite_mesure?: UniteMesure;
  tests_par_lot?: number | null;
  seuil_alerte?: number;
  statut?: ReactifStatut;
}

/** Reorder bucket for a reagent (GET /inventory/reactifs/:id/reorder). */
export type ReorderBucket = "OK" | "BAS" | "RUPTURE";

export interface Reorder {
  reactif_id: Id;
  /** Σ quantite_restante over actif lots. */
  on_hand: number;
  seuil: number;
  bucket: ReorderBucket;
}

/** Near-expiry tier for a lot (GET /inventory/reactifs/:id/risk row, FEFO-ordered). */
export type RiskTier = "critique" | "alerte" | "ok";

export interface RiskLot {
  lot_id: Id;
  numero_lot: string;
  days_remaining: number;
  tier: RiskTier;
}

// ── Lots + lifecycle ───────────────────────────────────────────────────────────

export type LotStatut = "actif" | "quarantaine" | "perime" | "epuise" | string;

/** Result of POST /inventory/lots. */
export interface LotReceived {
  lot_id: Id;
  numero_lot: string;
}

/** Result of POST /inventory/lots/:id/statut. */
export interface LotStatutResult {
  lot_id: Id;
  statut: LotStatut;
}

/**
 * A lot row across ALL lifecycle states (GET /inventory/lots, NOVA-027). Unlike the FEFO `risk` read
 * (actif-only), this lists quarantaine/perime/epuise too so the recall picker can target a recalled or
 * exhausted lot, not just an active one.
 */
export interface Lot {
  lot_id: Id;
  reactif_id: Id;
  reactif_code: string | null;
  reactif_libelle: string | null;
  discipline_code: string | null;
  numero_lot: string;
  date_peremption: string | null;
  quantite_restante: number;
  statut: LotStatut;
}

/** Result of POST /inventory/sweep — lot ids transitioned actif → perime. */
export interface SweepResult {
  swept: Id[];
}

// ── Per-lot movement history (NOVA-058) ──────────────────────────────────────────

/** A ledger movement type (append-only; quantite is signed for ajustement, 0 for peremption). */
export type MouvementType = "entree" | "sortie" | "ajustement" | "peremption" | string;

/** One row of the append-only MouvementStock ledger for a lot (GET /inventory/lots/:id/mouvements). */
export interface LotMouvement {
  id: string;
  type: MouvementType;
  quantite: number;
  /** Running balance AFTER this movement (recomputed server-side from the signed deltas). */
  solde_apres: number;
  motif: string | null;
  reference_liee: string | null;
  created_at: string;
}

/** The per-lot movement-history payload (lot header + the ordered ledger rows). */
export interface LotMovements {
  lot: {
    lot_id: Id;
    reactif_id: Id;
    numero_lot: string;
    statut: LotStatut;
    quantite_restante: number;
  };
  mouvements: LotMouvement[];
}

// ── Recette (consumption recipe) ─────────────────────────────────────────────────

/** Result of POST /inventory/recettes. */
export interface RecetteCreated {
  recette_id: Id;
}

// ── Charge (active lot on analyzer) ──────────────────────────────────────────────

/** Result of POST /inventory/charges. */
export interface ChargeResult {
  charge_id: Id;
  /** Id of the prior charge that was decharged, or null if none was active. */
  decharged_prior?: string | null;
}

// ── Lot → Resultat recall ────────────────────────────────────────────────────────

/** Result of POST /inventory/lots/:id/recall/preview (dry-run, no write). */
export interface RecallPreview {
  lot_id: Id;
  numero_lot: string;
  statut: LotStatut;
  /** Count of valide/corrige Resultats stamped with the lot. */
  affected_count: number;
  resultat_ids: string[];
}

/** Result of POST /inventory/lots/:id/recall/confirm. */
export interface RecallConfirm {
  lot_id: Id;
  statut: LotStatut;
  affected_count: number;
  /** True when the lot was already quarantined (re-confirm = no-op). */
  idempotent_noop: boolean;
}

// ── Cycle-count (INV-8 — NOVA-024) ──────────────────────────────────────────────

export type InventaireScope = "global" | "discipline" | "reactif";
export type InventaireStatut = "brouillon" | "en_cours" | "en_revue" | "postee" | "annulee" | string;

/** One counted line in a cycle-count session (a frozen lot snapshot + the physical count + the ecart). */
export interface LigneInventaire {
  ligne_id: string;
  lot_reactif_id: string;
  numero_lot: string;
  /** quantite_restante projection frozen at session open. */
  quantite_systeme: number;
  /** The physical count entered by the counter (null until counted). */
  quantite_comptee: number | null;
  /** comptee − systeme (null until counted). */
  ecart: number | null;
  /** The ajustement posted for this line, once the session is posted. */
  mouvement_id: string | null;
}

/** A cycle-count session (GET /inventory/inventaires/:id, the open/review/post payload shape). */
export interface SessionInventaire {
  session_id: string;
  libelle: string;
  scope: InventaireScope;
  discipline_code: string | null;
  reactif_id: string | null;
  statut: InventaireStatut;
  postee_at: string | null;
  lignes: LigneInventaire[];
}

/** One posted adjustment in the post summary. */
export interface AjustementInventaire {
  lot_id: string;
  ecart: number;
  mouvement_id: string;
}

/** Result of POST /inventory/inventaires/:id/post (the post summary). */
export interface PostInventaireResult {
  session_id: string;
  statut: InventaireStatut;
  ajustements: AjustementInventaire[];
  /** True when re-posting an already-posted session (no second ajustement). */
  idempotent_noop: boolean;
}
